import React, { useState } from 'react'
import styled from '@emotion/styled'
import { Button } from 'antd'
import { Integration } from './types'
import { useHistory } from 'react-router-dom'
import ConnectedIcon from 'packages/@slerp/assets/integrations/connected.svg'
import DisconnectedIcon from 'packages/@slerp/assets/integrations/disconnected.svg'

interface IntegrationCardProps {
  integration: Integration
}

const IntegrationCard = ({ integration }: IntegrationCardProps) => {
  const { active, slug, name, description, icon } = integration

  const [isActive, setIsActive] = useState(active)
  const history = useHistory()

  const handleClick = () => {
    history.push(`/integrations/${slug}`)
    setIsActive(!isActive)
  }

  return (
    <Root>
      <Card active={isActive}>
        <CardIcon src={icon} />
        <CardContent>
          {isActive && (
            <StatusTag>
              <span>Active</span>
            </StatusTag>
          )}
          <CardName>{name}</CardName>
          <CardDescription>{description}</CardDescription>
        </CardContent>
        <CardButton active={isActive} onClick={handleClick}>
          {isActive ? 'Manage' : 'Connect'}
        </CardButton>
      </Card>
      {isActive && (
        <Connections>
          <Connection>
            <ConnectionIcon src={ConnectedIcon} />
            <CardContent>
              <CardName size='sm'>You have 3 Connected Stores </CardName>
              <CardDescription>
                This means all orders automatically arrive in the POS, reducing
                manual work, mistakes, and missed orders.
              </CardDescription>
            </CardContent>
          </Connection>
          <Connection>
            <ConnectionIcon src={DisconnectedIcon} />
            <CardContent>
              <CardName size='sm'>You have 3 Connected Stores </CardName>
              <CardDescription>
                This means all orders automatically arrive in the POS, reducing
                manual work, mistakes, and missed orders.
              </CardDescription>
            </CardContent>
          </Connection>
        </Connections>
      )}
    </Root>
  )
}

const Root = styled.li`
  list-style: none;
  display: flex;
  flex-direction: column;

  border-radius: 4px;
  border: 1px solid #d2d2d9;
  background-color: #ffffff;
  box-shadow: 0px 5px 12px 0px #4a4a681a;
`

const ConnectionIcon = styled.img`
  width: 20px;
  height: 20px;
`

const Connections = styled.div`
  padding-top: 1px;
`

const Connection = styled.div`
  padding: 12px 0;
  margin: 0 16px;
  background-color: #ffffff;

  display: flex;
  align-items: center;
  gap: 12px;

  &:not(:last-of-type) {
    border-bottom: 1px solid #d2d2d9;
  }
`

const StatusTag = styled.div`
  width: fit-content;
  margin-bottom: 4px;

  font-size: 12px;
  font-weight: 500;
  line-height: 100%;
  color: #4eb21c;

  padding: 5px 8px;
  border-radius: 24px;
  background-color: #4eb21c1a;
`

const Card = styled.div<{ active: boolean }>(
  ({ active }) => `
  padding: 12px 16px;

  display: flex;
  align-items: center;
  gap: 12px;

  border-radius: 4px;
  border: 1px solid #D2D2D9;
  margin: -1px;
  background-color: ${active ? '#F6FAFF' : '#FFFFFF'};
`
)

const CardIcon = styled.img`
  width: 72px;
  height: 72px;
  border-radius: 4px;
  object-fit: contain;
  object-position: center;
`

const CardContent = styled.div`
  flex: 1;
`

const CardName = styled.p<{ size?: 'sm' }>(
  ({ size }) => `
  font-size: ${size === 'sm' ? '12px' : '14px'};
  font-weight: ${size === 'sm' ? 500 : 600};
  line-height: 100%;
  color: #4A4A68;
  margin-bottom: 4px;
`
)

const CardDescription = styled.p`
  font-size: 12px;
  font-weight: 400;
  line-height: 100%;
  color: #77778e;
  margin-bottom: 0;
`

const CardButton = styled(Button)<{ active: boolean }>(({ active }) => `
  background-color: ${active ? 'transparent' : '#4A4A68'};
  color: ${active ? '#4A4A68' : '#FFFFFF'};
  border-color: #4a4a68;

  &:hover,
  &:focus,
  &:active {
    background-color: ${active ? 'transparent' : '#4A4A68'};
    color: ${active ? '#4A4A68' : '#FFFFFF'};
    border-color: #4a4a68;
  }
`)

export default IntegrationCard
